import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import {
  energyProvenanceRows,
  formatDistance,
  formatKcal,
  type ResolvedWorkoutCalories,
} from "@heraion/shared";

import { Icon } from "../Icon/Icon";
import { IconButton } from "../primitives/IconButton";
import { MetricBlock, metricValueTextStyle } from "../primitives/MetricBlock";
import { useDisclosure } from "../primitives/useDisclosure";
import type { UiTheme } from "../theme";
import { makeCardBoxStyle } from "../primitives/cardBoxStyle";

// HC-8 — the workout energy readout: calories (estimate-vs-measured, resolved at
// read) + effort (HR + %HRmax). ONE shared block for every surface it appears on
// — mobile logger footer (live during a workout + on edit reopen), the share
// card, the web-rnw session detail — mounted thin, never re-rolled per app
// ([[feedback_web_means_web_rnw]]).
//
// Provenance follows the "no hardcoded source label" rule
// ([[feedback_metric_source_provenance_registry]]): the value reads clean; an
// unobtrusive (i) reveals the source(s) BY NAME from the registry (extensible to
// future 3rd-party sources), with the resolver-picked one marked — no inline
// "estimated/measured" commentary. Effort is FACTUAL (HR + %HRmax), never a zone
// interpretation (HC-8 exposes; the coach interprets).
export type CaloriesEffortBlockProps = {
  theme: UiTheme;
  /** From `resolveWorkoutCalories(session,{hcWins})`; null when the session
   *  carries no calorie figure at all (nothing to show). */
  resolved: ResolvedWorkoutCalories | null;
  /** `Session.externalOrigin` — resolves the measured value's source name. */
  externalOrigin?: string;
  avgHrBpm?: number;
  peakHrBpm?: number;
  /** %HRmax; the caller computes `percentHrMax(avgHrBpm, ageFromBucket(bucket))`. */
  hrMaxPct?: number | null;
  /** #156: total cardio distance in metres (from the readout); omitted = no block. */
  distanceM?: number;
  /** false on the STATIC share image — hides the interactive (i) provenance pill
   *  (the canonical number still shows). */
  showSources?: boolean;
  /** LG-46 — the calorie figure is a LIVE projection: it counts the set the
   *  athlete is about to do, not just work already logged. When set, the (i)
   *  provenance disclosure says so; the number itself stays clean (no inline
   *  "estimated" label — that's the (i)'s job). Never set on a saved session
   *  (nothing is proposed) or the static share card. */
  projected?: boolean;
  /** Opt-in card wrapper (surface bg + border + radius + padding, matching
   *  this surface's sibling cards — WorkoutTotalsCard/WorkoutHeatMap in the
   *  logger footer) so the block stops floating with only a divider (DS-18).
   *  Default OFF: `<SessionSummaryView>` already renders this block inside
   *  its own card, so double-boxing there would nest a card in a card —
   *  only the logger footer (where it sits bare beside boxed siblings)
   *  opts in. */
  boxed?: boolean;
};

export function CaloriesEffortBlock({
  theme,
  resolved,
  externalOrigin,
  avgHrBpm,
  peakHrBpm,
  hrMaxPct,
  distanceM,
  showSources = true,
  projected = false,
  boxed = false,
}: CaloriesEffortBlockProps) {
  const { t, i18n } = useTranslation();
  const styles = makeStyles(theme);
  const { expanded, toggle } = useDisclosure();

  const rows = resolved ? energyProvenanceRows(resolved, externalOrigin) : [];
  const hasEffort =
    avgHrBpm !== undefined ||
    peakHrBpm !== undefined ||
    (hrMaxPct ?? null) !== null;

  const hasDistance = distanceM !== undefined && distanceM > 0;

  if (!resolved && !hasEffort && !hasDistance) return null;

  const content = (
    <>
      {resolved ? (
        <MetricBlock
          theme={theme}
          title={t("mobile.postSession.blockCalories", { defaultValue: "Calories" })}
        >
          <View style={styles.valueRow}>
            <Text style={styles.value}>{formatKcal(resolved.kcal)}</Text>
            {showSources && rows.length > 0 ? (
              <IconButton
                theme={theme}
                name="info"
                expanded={expanded}
                active={expanded}
                onPress={toggle}
                accessibilityLabel={t("mobile.energySource.pillLabel", {
                  defaultValue: "Calorie sources",
                })}
              />
            ) : null}
          </View>
          {showSources && expanded ? (
            <View style={styles.sources}>
              {rows.map((row) => (
                <View
                  key={row.source.id}
                  style={styles.sourceRow}
                  accessible
                  accessibilityLabel={[
                    t(row.source.labelKey),
                    formatKcal(row.kcal),
                    row.active
                      ? t("mobile.energySource.selected", { defaultValue: "selected" })
                      : "",
                  ]
                    .filter(Boolean)
                    .join(", ")}
                >
                  <View style={styles.sourceMark}>
                    {row.active ? (
                      <Icon
                        name="check"
                        size={theme.typography.sizes.sm}
                        color={theme.palette.ink}
                      />
                    ) : null}
                  </View>
                  <Text
                    style={[styles.sourceName, row.active && styles.sourceActive]}
                    numberOfLines={1}
                  >
                    {t(row.source.labelKey)}
                  </Text>
                  <Text
                    style={[styles.sourceKcal, row.active && styles.sourceActive]}
                  >
                    {formatKcal(row.kcal)}
                  </Text>
                </View>
              ))}
              {projected ? (
                <Text style={styles.projectionNote}>
                  {t("mobile.energySource.projectionNote", {
                    defaultValue:
                      "Includes the set you're about to do: a projection of the whole session, not work you've already logged.",
                  })}
                </Text>
              ) : null}
            </View>
          ) : null}
        </MetricBlock>
      ) : null}

      {hasEffort ? (
        <MetricBlock
          theme={theme}
          title={t("mobile.postSession.blockEffort", { defaultValue: "Effort" })}
        >
          {avgHrBpm !== undefined || peakHrBpm !== undefined ? (
            <Text style={styles.value}>
              {t("mobile.postSession.effortHr", {
                defaultValue: "{{avg}} avg · {{peak}} peak bpm",
                avg: avgHrBpm ?? "—",
                peak: peakHrBpm ?? "—",
              })}
            </Text>
          ) : null}
          {(hrMaxPct ?? null) !== null ? (
            <Text style={styles.hrMax}>
              {t("mobile.postSession.effortHrMax", {
                defaultValue: "{{pct}}% HRmax",
                pct: hrMaxPct,
              })}
            </Text>
          ) : null}
        </MetricBlock>
      ) : null}

      {hasDistance ? (
        <MetricBlock
          theme={theme}
          title={t("mobile.postSession.blockDistance", { defaultValue: "Distance" })}
          value={formatDistance(distanceM ?? 0, i18n.language)}
        />
      ) : null}
    </>
  );

  return boxed ? <View style={styles.box}>{content}</View> : content;
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    // Opt-in card chrome (`boxed`). Comes from makeCardBoxStyle, the ONE box
    // literal WorkoutTotalsCard and WorkoutHeatMap now share too: writing it
    // by hand here would have been the third identical copy (DS-18 review).
    box: makeCardBoxStyle(theme),
    valueRow: { flexDirection: "row", alignItems: "center", gap: s.s2 },
    // Shared metric-value look (ink · lg · 700 · tabular) — rendered inline here
    // because the number shares its row with the (i) pill.
    value: metricValueTextStyle(theme),
    sources: { gap: s.s2, paddingTop: s.s1 },
    sourceRow: { flexDirection: "row", alignItems: "center", gap: s.s2 },
    // Fixed mark column so source names align whether or not the check shows.
    sourceMark: { width: ty.sizes.sm, alignItems: "center" },
    sourceName: { flex: 1, color: p.ink2, fontSize: ty.sizes.sm },
    sourceKcal: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontVariant: ["tabular-nums"],
    },
    sourceActive: { color: p.ink, fontWeight: "700" },
    // LG-46 — the projection caveat, quieter than the source rows (it explains,
    // it isn't itself a source), sitting at the foot of the (i) disclosure.
    projectionNote: { color: p.ink2, fontSize: ty.sizes.sm, paddingTop: s.s1 },
    hrMax: { color: p.ink2, fontSize: ty.sizes.sm },
  });
}
